import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Snackbar,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import DealerHeader from "../components/DealerHeader";
import {
  DURATIONS,
  PACKAGES,
  RATE_DIVISOR,
  ZONES,
  getStoredRateTable,
  saveRateTable,
} from "./ratesConfig";

export default function AdminRates() {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState("Generali Assicurazioni");
  const [productName, setProductName] = useState("Mobility Safe SAT - CVT");
  const [mainTab, setMainTab] = useState(0);
  const [zoneTab, setZoneTab] = useState(0);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const [editableRates, setEditableRates] = useState(() => getStoredRateTable());

  const currentZone = ZONES[zoneTab];

  const stats = useMemo(() => {
    let missing = 0;

    ZONES.forEach((zone) => {
      PACKAGES.forEach((pkg) => {
        DURATIONS.forEach((duration) => {
          const value = editableRates[zone]?.[pkg]?.[duration];
          if (value === null || value === undefined || Number.isNaN(Number(value))) {
            missing += 1;
          }
        });
      });
    });

    return { missing };
  }, [editableRates]);

  const showToast = (message, severity = "success") => {
    setToast({ open: true, message, severity });
  };

  const handleRateChange = (zone, pkg, duration, value) => {
    const parsed = value === "" ? "" : Number(value);

    setEditableRates((prev) => ({
      ...prev,
      [zone]: {
        ...prev[zone],
        [pkg]: {
          ...prev[zone][pkg],
          [duration]: parsed,
        },
      },
    }));
  };

  const handleValidate = () => {
    if (stats.missing > 0) {
      showToast(`Validazione fallita: ${stats.missing} valori mancanti o non validi`, "error");
      return;
    }

    showToast("Tabella valida: nessuna anomalia trovata", "success");
  };

  const handleSave = () => {
    const saved = saveRateTable(editableRates);

    if (!saved) {
      showToast("Salvataggio non riuscito: dati tabella non validi", "error");
      return;
    }

    showToast("Modifiche salvate", "success");
  };

  const handleExportCsv = () => {
    let csv = `Compagnia,${companyName}\nProdotto,${productName}\n\nZona,Pacchetto,12,24,36,48,60\n`;

    ZONES.forEach((zone) => {
      PACKAGES.forEach((pkg) => {
        const row = DURATIONS.map((duration) => editableRates[zone]?.[pkg]?.[duration] ?? "").join(",");
        csv += `${zone},${pkg},${row}\n`;
      });
    });

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "admin_rates.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showToast("CSV esportato correttamente", "success");
  };

  return (
    <Box sx={{ backgroundColor: "background.default", minHeight: "100vh" }}>
      <Container maxWidth="xl" sx={{ pt: 3.5 }}>
        <DealerHeader
          showTabs={false}
          actionLabel="Torna ai Preventivi"
          onActionClick={() => navigate("/pages/dealer")}
        />
      </Container>

      <Container maxWidth="xl" sx={{ py: 3.5 }}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nome Compagnia"
                  value={companyName}
                  onChange={(event) => setCompanyName(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nome Prodotto"
                  value={productName}
                  onChange={(event) => setProductName(event.target.value)}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.25,
              alignItems: "center",
            }}
          >
            <Button variant="contained" color="success" onClick={handleSave}>
              Salva Modifiche
            </Button>
            <Button variant="outlined" onClick={handleValidate}>
              Valida
            </Button>
            <Button variant="outlined" component="label">
              Importa Excel
              <input type="file" accept=".xlsx,.xls" hidden onChange={() => showToast("Import Excel completato", "success")} />
            </Button>
            <Button variant="outlined" onClick={handleExportCsv}>
              Esporta CSV
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Tabs value={mainTab} onChange={(_, value) => setMainTab(value)} sx={{ mb: 2 }}>
              <Tab label="Tabella Tassi" />
              <Tab label="Scoperti e Franchigie" />
            </Tabs>

            {mainTab === 0 && (
              <Box>
                <Tabs
                  value={zoneTab}
                  onChange={(_, value) => setZoneTab(value)}
                  variant="scrollable"
                  allowScrollButtonsMobile
                  sx={{ mb: 2, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
                >
                  {ZONES.map((zone) => (
                    <Tab key={zone} label={`Zona ${zone}`} />
                  ))}
                </Tabs>

                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, minWidth: 140 }}>Pacchetto</TableCell>
                        {DURATIONS.map((duration) => (
                          <TableCell key={duration} align="center" sx={{ fontWeight: 700 }}>
                            {duration} mesi
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {PACKAGES.map((pkg) => (
                        <TableRow key={pkg}>
                          <TableCell sx={{ fontWeight: 600 }}>{pkg}</TableCell>
                          {DURATIONS.map((duration) => (
                            <TableCell key={duration} align="center">
                              <TextField
                                type="number"
                                value={editableRates[currentZone]?.[pkg]?.[duration] ?? ""}
                                onChange={(event) =>
                                  handleRateChange(currentZone, pkg, duration, event.target.value)
                                }
                                size="small"
                                sx={{ width: 96 }}
                                inputProps={{
                                  step: 0.01,
                                  style: { textAlign: "center" },
                                }}
                              />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  I tassi sono in per mille (‰). Premio = Valore × (Tasso / {RATE_DIVISOR})
                </Typography>
              </Box>
            )}

            {mainTab === 1 && (
              <Card variant="outlined">
                <CardContent
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography variant="h6">Scoperti e Franchigie</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Gestisci le condizioni applicate per zona e garanzia.
                    </Typography>
                  </Box>
                  <Button variant="contained" onClick={() => navigate("/pages/dealer/admin-conditions")}>
                    Gestisci Condizioni
                  </Button>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>
      </Container>

      <Snackbar
        open={toast.open}
        autoHideDuration={2500}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
